import { Divider, Grid, List, ListItem, ListItemText } from '@material-ui/core';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CheckBox, Switch } from '../../../../components/common/Inputs';
import Typography from '../../../../components/common/Typography';
import { useForm } from '../../../../hooks/useForm';
import { useTrans } from '../../../../services/i18n';
import { schemaToInitialState } from '../../../../utils/validationUtils';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useScheme } from '../scheme';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { Button } from '../../../../components/common/Buttons';
import { ModalDialog } from '../../../../components/common/Dialogs';
import { IconSMSNotification } from '../../../../components/svg';
import NotificationFrequencyField from './NotificationFrequencyField';
import { hasCrmEmailPreference, hasCrmRecapPreference } from '../crmEmailFrequencies';
import {
  useNotificationSettingsSubmit,
  useNotificationSwitches,
  notificationSettingsUserShape,
} from '../notificationSettings';

const useStyles = makeStyles((theme) => ({
  containerCard: {
    padding: '0rem 0rem',
    width: '96%',
  },
  containerFooter: {
    padding: '0rem 2.3rem 1.87rem 0rem',
    marginTop: 'unset',
  },
  noTopPadding: {
    paddingTop: 'unset',
    paddingBottom: 'unset',
  },
  checksContainer: {
    marginLeft: '3.2rem',
  },
  forMargin: {
    marginLeft: '0.6rem',
  },
  includeMargin: {
    marginTop: 'unset',
    marginLeft: '0.6rem',
  },
  divider: {
    marginTop: '0.5rem',
  },
  dailyRecapList: {
    paddingBottom: '0rem',
  },
  modalTitle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cadenceNote: {
    marginLeft: '3.2rem',
    marginTop: theme.spacing(1),
  },
}));

const UPDATE_FIELDS = [
  { name: 'taskReminders', label: 'pipelines-pursuits-tasks-reminders', xs: 6 },
  { name: 'savedSearchNotices', label: 'saved-searches', xs: 5 },
];

const CRM_UPDATES_FIELD = { name: 'crmUpdates', label: 'crm-updates', xs: 6 };

const DAILY_RECAP_FIELDS = [
  { name: 'pursuitDailyRecap', label: 'pursuit-notices', xs: 5 },
  { name: 'savedSearchDailyRecap', label: 'saved-searches', xs: 6 },
  { name: 'taskRemindersDailyRecap', label: 'pipelines-pursuits-tasks-reminders', xs: 6 },
  { name: 'personalTaskRemindersDailyRecap', label: 'personal-tasks-reminders', xs: 6 },
  { name: 'teamUpdatesDailyRecap', label: 'team-updates', xs: 5 },
];

const DAILY_RECAP_HEADING_ID = 'modal-daily-recap-heading';

const CRM_RECAP_FIELD = {
  name: 'crmDailyRecap',
  label: 'crm-updates',
  xs: 6,
  id: 'modal-crm-daily-recap',
  labelId: 'modal-crm-daily-recap-label',
};

export default function EmailNotificationModal({ user, onUpdate, open, onClose }) {
  const isMobile = useIsMobile('xs');
  const { trans } = useTrans();
  const schema = useScheme(trans);
  const [step, setStep] = useState(1);
  const styles = useStyles();
  const crmAvailable = hasCrmEmailPreference(user);
  const recapAvailable = hasCrmRecapPreference(user);
  const { onSubmit, pending } = useNotificationSettingsSubmit({
    crmAvailable,
    recapAvailable,
    onUpdate,
    onClose,
    onFailure: () => setFormValues(),
  });

  const { setValues, getFieldProps, handleSubmit, values } = useForm(schema, schemaToInitialState(schema), onSubmit, {
    validateOnChange: false,
    validateOnBlur: false,
  });
  const { updateNoti, dailyRecapEmails } = useNotificationSwitches(user, values, crmAvailable, recapAvailable);

  /***
   *
   */
  React.useEffect(() => {
    setFormValues();
  }, [user, setValues]);

  /***
   * Set the form with the user values
   */
  const setFormValues = () => {
    setValues({
      pursuitNotices: Boolean(user?.pursuitNotices) || false,
      savedSearchNotices: Boolean(user?.savedSearchNotices) || false,
      teamUpdates: Boolean(user?.teamUpdates) || false,
      onEmailNotifications: Boolean(user?.onEmailNotifications) || true,
      taskReminders: Boolean(user?.taskReminders) || false,
      //Daily-recap
      pursuitDailyRecap: Boolean(user?.pursuitDailyRecap) || false,
      savedSearchDailyRecap: Boolean(user?.savedSearchDailyRecap) || false,
      teamUpdatesDailyRecap: Boolean(user?.teamUpdatesDailyRecap) || false,
      taskRemindersDailyRecap: Boolean(user?.taskRemindersDailyRecap) || false,
      personalTaskRemindersDailyRecap: Boolean(user?.personalTaskRemindersDailyRecap) || false,
      crmUpdates: crmAvailable ? Boolean(user.crmUpdates) : false,
      crmEmailFrequency: crmAvailable ? user.crmEmailFrequency : '',
      crmDailyRecap: recapAvailable ? Boolean(user.crmDailyRecap) : false,
    });

    // No Help notification modal
    setStep(2);
  };

  /***
   *
   */
  const onChangeUpdateNoti = (switchValue) => {
    // Set values according to switch value
    setValues({
      ...values,
      pursuitNotices: true, // Always receive this kind of notification
      savedSearchNotices: switchValue ? true : false, // It indicates whether emails are received or not
      teamUpdates: true, // Always receive this kind of notification
      onEmailNotifications: true,
      taskReminders: switchValue ? true : false, // It indicates whether emails are received or not
      crmUpdates: crmAvailable ? switchValue : false,
    });
  };

  /***
   *
   */
  const onChangeDailyRecap = (switchValue) => {
    // Set values according to switch value
    setValues({
      ...values,
      pursuitDailyRecap: switchValue ? true : false,
      savedSearchDailyRecap: switchValue ? true : false,
      teamUpdatesDailyRecap: switchValue ? true : false,
      taskRemindersDailyRecap: switchValue ? true : false,
      personalTaskRemindersDailyRecap: switchValue ? true : false,
      crmDailyRecap: recapAvailable ? switchValue : false,
    });
  };

  const handleCloseForm = () => {
    onClose();
    setFormValues();
  };

  const isChecked = true;

  const renderSection = ({
    listClassName,
    itemClassName,
    checked,
    onChange,
    title,
    headingId,
    includeClassName,
    fields,
    children,
  }) => (
    <List className={listClassName}>
      <ListItem disableGutters className={itemClassName}>
        <Switch
          checked={checked}
          onChange={(ev) => onChange(ev.target.checked)}
          inputProps={headingId ? { 'aria-labelledby': headingId } : undefined}
        />
        <ListItemText>
          <Typography id={headingId} variant="h6" weight="600" color={isChecked ? 'initial' : 'disabled'}>
            {trans(title)}
          </Typography>
        </ListItemText>
      </ListItem>

      <Grid container lg={12} md={12} xs={12} className={styles.checksContainer}>
        <Grid lg={12} md={12} xs={12}>
          <Typography variant="subtitle1" weight="600" color={'disabled'} className={includeClassName}>
            {trans('include')}
          </Typography>
        </Grid>
        {fields.map(({ name, label, xs, id, labelId }) => (
          <Grid key={name} lg={6} md={6} sm={6} xs={xs}>
            <Typography style={{ fontSize: isMobile ? '12px' : '13px' }} weight="400">
              <CheckBox
                color="secondary"
                id={id}
                checked={values[name]}
                inputProps={labelId ? { 'aria-labelledby': `${labelId} ${headingId}` } : undefined}
                {...getFieldProps(name)}
              />
              {labelId ? <span id={labelId}>{trans(label)}</span> : trans(label)}
            </Typography>
          </Grid>
        ))}
      </Grid>
      {children}
    </List>
  );

  return (
    <ModalDialog
      open={open}
      onClose={handleCloseForm}
      title={
        <>
          <div className={styles.modalTitle}>
            <IconSMSNotification />
          </div>
          <div className={styles.modalTitle} style={{ fontSize: isMobile ? '15px' : '21px' }}>
            {trans('email-notification-settings')}
          </div>
        </>
      }
      direction={isMobile ? 'up' : 'down'}
      actions={
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="outlined" color="primary" disabled={pending} onClick={handleCloseForm}>
              {trans('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={pending}
              pending={pending}
              onClick={() => {
                handleSubmit();
              }}
            >
              {pending ? trans('Saving') : trans('save')}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Grid container className={styles.containerCard}>
        <Grid item lg={12} md={12} sm={12} xs={12} className={styles.noTopPadding}>
          {renderSection({
            listClassName: styles.noTopPadding,
            itemClassName: styles.noTopPadding,
            checked: updateNoti,
            onChange: onChangeUpdateNoti,
            title: 'update-notifications',
            includeClassName: styles.forMargin,
            fields: crmAvailable ? [...UPDATE_FIELDS, CRM_UPDATES_FIELD] : UPDATE_FIELDS,
            children: crmAvailable && updateNoti && values.crmUpdates && (
              <NotificationFrequencyField
                value={values.crmEmailFrequency}
                onChange={(frequency) => setValues({ ...values, crmEmailFrequency: frequency })}
              />
            ),
          })}

          {values.savedSearchNotices && (
            <Typography variant="body2" className={styles.cadenceNote}>
              {trans('saved-search-emails-sent-once-a-day')}
            </Typography>
          )}

          <Divider light className={styles.divider} />

          {renderSection({
            itemClassName: styles.dailyRecapList,
            checked: dailyRecapEmails,
            onChange: onChangeDailyRecap,
            title: 'daily-recap-emails',
            headingId: DAILY_RECAP_HEADING_ID,
            includeClassName: styles.includeMargin,
            fields: recapAvailable ? [...DAILY_RECAP_FIELDS, CRM_RECAP_FIELD] : DAILY_RECAP_FIELDS,
          })}
        </Grid>
      </Grid>
    </ModalDialog>
  );
}

EmailNotificationModal.propTypes = {
  user: notificationSettingsUserShape.isRequired,
  onUpdate: PropTypes.func.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
